import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { parseJSON } from 'date-fns';
import { z } from 'zod';

const questionTypeSchema = z.enum(['MCSA', 'MCMA', 'SFA']);
const optionsTypeSchema = z.enum(['TEXT', 'EMOJI']);

const studentSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

const resultSchema = z.object({
  correct: z.array(studentSchema),
  incorrect: z.array(studentSchema),
  partiallyCorrect: z.array(studentSchema).optional(),
});

const responseSchema = z.object({
  student: studentSchema,
  response: z.string().optional().nullable(),
  selectedOptions: z.array(z.string()).optional().nullable(),
  date: z
    .string()
    .optional()
    .nullable()
    .transform<Date | undefined>((d) => (d ? parseJSON(d) : undefined)),
});

const questionSchema = z.object({
  activityType: z
    .enum([
      'QUIZ',
      'EXIT_TICKET',
      'DISCUSSION_QUESTIONS',
      'SHORT_WRITE',
      'POLL',
      'ANTICIPATION_GUIDE',
    ])
    .optional(),
  question: z.string(),
  questionId: z.string(),
  type: questionTypeSchema,
  options: z.array(z.string()).optional().nullable(),
  optionsType: optionsTypeSchema.optional().nullable().default('TEXT'),
  results: resultSchema.optional(),
  correctOptions: z.array(z.string()).optional().nullable(),
  selectedOptions: z.array(z.string()).optional(),
  responses: z.array(responseSchema).optional().nullable(),
  response: z.string().optional().nullable(),
  avgCorrect: z.number().optional(),
  standards: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  result: z
    .enum(['CORRECT', 'INCORRECT', 'PARTIALLY_CORRECT'])
    .optional()
    .nullable(),
});

const activityResultsSchema = z.object({
  title: z.string().optional().nullable(),
  directions: z.string().nullable().optional(),
  items: z
    .array(
      z.object({
        questions: z.array(questionSchema),
      })
    )
    .nullable()
    .optional(),
  STARTED: z.union([z.boolean(), z.number()]).optional(),
  activityType: z
    .enum([
      'QUIZ',
      'EXIT_TICKET',
      'DISCUSSION_QUESTIONS',
      'SHORT_WRITE',
      'POLL',
    ])
    .optional()
    .nullable(),
});

export type MetricsActivityResultStudent = z.infer<typeof studentSchema>;
export type MetricsActivityResult = z.infer<typeof resultSchema>;
export type MetricsActivityQuestion = z.infer<typeof questionSchema>;
export type MetricsActivityResults = z.infer<typeof activityResultsSchema>;

export type MetricActivityType =
  | 'quizResponse'
  | 'studentQuizResponse'
  | 'exitTickets'
  | 'studentExitTicketResponse'
  | 'shortWrites'
  | 'studentShortWriteResponse';

export interface MetricsActivityResultsApiProps {
  metricType: MetricActivityType;
  orgId: string;
  teacherId: string;
  product: string;
  classId?: string;
  contentId: string;
  activityId?: string;
  studentId?: string;
}

const getMetricsActivityApi = ({
  metricType,
  orgId,
  teacherId,
  product,
  classId,
  contentId,
  activityId,
  studentId,
}: MetricsActivityResultsApiProps) =>
  fetcher('/metrics-activity', {
    queryParameters: {
      metricType,
      orgId,
      teacherId,
      product,
      classId,
      contentId,
      activityId,
      studentId,
    },
  }).then((res) => activityResultsSchema.parse(res));

export function useMetricsActivityApi({
  metricType,
  orgId,
  teacherId,
  product,
  classId,
  contentId,
  activityId,
  studentId,
}: MetricsActivityResultsApiProps): UseQueryResult<
  MetricsActivityResults,
  Error
> {
  return useQuery({
    queryKey: [
      'metrics-activity',
      metricType,
      orgId,
      teacherId,
      product,
      classId,
      contentId,
      activityId,
      studentId,
    ].filter(Boolean),
    queryFn: () =>
      getMetricsActivityApi({
        metricType,
        orgId,
        teacherId,
        product,
        classId,
        contentId,
        activityId,
        studentId,
      }),
    enabled: !!metricType && !!orgId && !!teacherId,
  });
}
